<template>
	<div>
		<div class="d-flex justify-content-end mb-2">
			<DateRangeFilter :from-date.sync="from_date" :to-date.sync="to_date" @change="getData()" />
		</div>

		<DashboardHero
			title="Broiler"
			:subtitle="heroSubtitle"
			snapshot-title="Feed & placement snapshot"
			:hero-stats="heroStats"
			:snapshot-stats="snapshotStats"
			:alerts="alerts"
			@stat-click="broilerDetails"
		>
			<template #chart>
				<h3 style="margin-bottom: 4px">Production trend</h3>
				<ChartjsLineChart />
			</template>
		</DashboardHero>

		<BirdSalesWidget bird-type="broiler" class="mt-2" @summary="onMarketSummary" />
	</div>
</template>

<script>
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import {
	BCard,
	BCardHeader,
	BCardTitle,
	BCardText,
	BCardBody,
	BRow,
	BCol,
	BMedia,
	BMediaAside,
	BAvatar,
	BMediaBody,
	BCarousel,
	BCarouselSlide,
	BButton,
	BImg,
	BFormDatepicker,
} from "bootstrap-vue";

import ChartjsLineChart from "../../charts-and-maps/charts/chartjs/ChartjsLineChart.vue";
import DashboardHero from "../components/DashboardHero.vue";
import BirdSalesWidget from "../components/BirdSalesWidget.vue";
import DateRangeFilter from "@core/components/date-range-filter/DateRangeFilter.vue";
import BroilerDashboard from "../dashboard/BroilerDashboard.vue";
import BreederDashboard from "../dashboard/BreederDashboard.vue";
import LayerDashboard from "../dashboard/LayerDashboard.vue";
import axiosIns from "@/libs/axios";
import VueApexCharts from "vue-apexcharts";
import vmson from "@/libs/empty";
import moment from "moment";

export default {
	components: {
		BRow,
		BCol,
		BCard,
		BCardHeader,
		BCardTitle,
		BCardText,
		BCardBody,
		BMedia,
		BAvatar,
		BMediaAside,
		BMediaBody,
		BButton,
		BroilerDashboard,
		BreederDashboard,
		LayerDashboard,
		VueApexCharts,
		BCarousel,
		BCarouselSlide,
		BImg,
		BFormDatepicker,
		ChartjsLineChart,
		DashboardHero,
		DateRangeFilter,
		BirdSalesWidget,
	},
	data: () => {
		return {
			broilerInfo: null,
			marketAlerts: [],
			slide: null,
			sliding: null,
			fromDate: null,
			toDate: null,
			from_date: null,
			to_date: null,
			carouselItems: [],
			series: [44],
			search_filter: {
				input_field: "",
				to_date: "",
				from_date: "",
			},
			total_birds: null,
			feed_stock: null,
			ready_to_sale: null,
			chick_placement: null,
			avg_body_weight: null,
			fcr: null,
			mortality_rate: null,
			farmId: null,
		};
	},
	computed: {
		heroSubtitle() {
			const from = this.from_date || this.fromDate;
			const to = this.to_date || this.toDate;
			return from && to ? `${from} to ${to}` : "";
		},
		heroStats() {
			const info = this.broilerInfo;
			return [
				{ label: "No of Birds", value: info ? info.totalBirds : 0 },
				{ label: "Mortality Rate", value: `${info ? info.mortalityRate : 0}%` },
				{ label: "FCR", value: `${info ? info.FCR : 0}` },
				{ label: "Avg Body Weight", value: info ? info.avgBodyWeight : 0 },
			];
		},
		snapshotStats() {
			const info = this.broilerInfo;
			return [
				{
					label: "Feed Stock",
					value: info && info.feedStock ? info.feedStock : 0,
					icon: require("@/assets/images/dashboard/feed_stock.svg"),
				},
				{
					label: "Chick Placement",
					value: info ? info.chickPlacement : 0,
					icon: require("@/assets/images/dashboard/chick_placement.svg"),
				},
			];
		},
		alerts() {
			const list = [];
			const info = this.broilerInfo;
			if (info) {
				const mortality = parseFloat(info.mortalityRate);
				if (!isNaN(mortality)) {
					if (mortality > 5) {
						list.push({ chip: "Mortality", variant: "critical", text: `Mortality rate is ${mortality}%, above the 5% concern threshold.` });
					} else if (mortality > 3) {
						list.push({ chip: "Mortality", variant: "warn", text: `Mortality rate is ${mortality}%, trending high.` });
					}
				}
			}
			// Marketplace action items (bids/orders waiting) surface here too.
			return [...list, ...this.marketAlerts];
		},
	},
	created() {
		this.fromDate = moment().clone().startOf("month").format("YYYY-MM-DD");
		this.toDate = moment().clone().endOf("month").format("YYYY-MM-DD");
		this.farmId = this.$route.params.farmId;
		this.getData();
	},
	methods: {
		onMarketSummary(s) {
			const list = [];
			if (s && s.pending_bids > 0) {
				list.push({ chip: "Bids", variant: "warn", text: `${s.pending_bids} lifter bid${s.pending_bids > 1 ? "s are" : " is"} waiting for your decision.` });
			}
			if (s && s.pending_orders > 0) {
				list.push({ chip: "Orders", variant: "critical", text: `${s.pending_orders} buyer order${s.pending_orders > 1 ? "s need" : " needs"} approval before dispatch.` });
			}
			this.marketAlerts = list;
		},
		broilerDetails(type) {
			let farmId = this.$route.params.farmId;
			if (farmId) {
				this.$router.push(`/apps/manage-farm/${farmId}/dashboard/broiler`);
			} else {
				this.$router.push({
					path: `/apps/dashboard/broiler`,
				});
			}
		},
		getData(id) {
			let url = "";
			let farmId = null;
			if (id) {
				farmId = id;
			} else if (this.farmId) {
				farmId = this.farmId;
			}

			if (farmId) {
				if (this.from_date != null && this.to_date != null) {
					url = `web/broiler-data/${farmId}/dashboard?from_date=${this.from_date}&to_date=${this.to_date}`;
				} else {
					url = `web/broiler-data/${farmId}/dashboard?from_date=${this.fromDate}&to_date=${this.toDate}`;
				}
			} else {
				if (this.from_date != null && this.to_date != null) {
					url = `web/broiler-data?from_date=${this.from_date}&to_date=${this.to_date}`;
				} else {
					url = `web/broiler-data?from_date=${this.fromDate}&to_date=${this.toDate}`;
				}
			}
			axiosIns
				.get(url)
				.then((response) => {
					this.broilerInfo = response.data.broiler_data;
				})
				.catch((error) => {
					this.$toast({
						component: ToastificationContent,
						position: "top-right",
						props: {
							icon: "CoffeeIcon",
							variant: "danger",
							text:
								error.response?.data?.message ||
								error.response?.data?.error ||
								(error.response?.data?.errors
									? Object.values(error.response.data.errors).flat().join(", ")
									: "Something went wrong. Please try again."),
						},
					});
				});
		},
		onSlideStart(slide) {
			this.sliding = true;
		},
		onSlideEnd(slide) {
			this.sliding = false;
		},
	},
	mounted() {
		vmson.$on("farmIdEvent", (id) => {});
	},
};
</script>
<style lang="scss">
.mb-25 {
	font-size: 1rem !important;
}
.chart {
	// display: flex;
	justify-content: center;
}
.card-value {
	align-self: center;
	text-align: center;
}
.carousel-item {
	background: transparent !important;
}
.carousel-item img {
	height: 200px !important ;
}
.carousel-indicators li {
	display: none;
}
.item {
	background: transparent;
	height: 200px;
	width: 600px !important;
	object-fit: contain !important;
	margin-bottom: 1rem;
}
.item.active {
	background: transparent !important;
}
.carousel-control-prev,
.carousel-control-next {
	display: none;
}

.card:hover {
	cursor: pointer;
}
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/pages/hatchery.scss";
@import "@core/scss/vue/pages/_common.scss";
</style>
